import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { NgModule, ModuleWithProviders, Provider, Type, ErrorHandler, EnvironmentProviders, inject, provideAppInitializer } from '@angular/core';

import { AppComponent } from './app.component';
import { HttpClient } from '@angular/common/http';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ConfiguratorUiModule } from '@icc/configurator/ui';
import { AppRoutingModule } from './app-routing.module';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import {
    initialState as rootInitialState,
    configuratorReducer,
    configuratorRootReducer,
} from './+state/root.reducer';
import { ConfiguratorEffects } from './+state/root.effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';

import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { WindowDoorConfiguratorModule } from '@icc/window-door/configurator';
import { PreviewComponent } from './preview/preview.component';
import { IccDrawModule } from '@icc/draw';
import { InitService } from './init.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { TranslateModule, TranslateLoader, TranslateParser, TranslateCompiler, MissingTranslationHandler, TranslateService as NgxTranslateService, TranslateStore } from '@ngx-translate/core';
import {
    SharedModule,
    SharedFacade,
    StepsService,
    IccTranslateParser,
    TranslateMessageFormatCompiler,
} from '@icc/configurator/shared';
import { NotificationsPanelComponent } from './notifications-panel/notifications-panel.component';
import { ConfiguratorConfigurationsService } from './configurations.service';
import { APP_CONFIG, AppConfig, AppConfigFactory } from '@icc/shared/config';
import { EventBusService } from '@icc/shared/event-bus.service';
import { ConfiguratorsDataService } from '@icc/shared/configurators/configurators-data.service';
import { ConfiguratorConfiguratorsDataService } from './configurators-data.service';
import { DatabaseManagerToken } from '@icc/shared/helpers/browser';
import { ConfiguratorDatabaseManager, LOCAL_DB_VERSION } from './database-manager';
import { ConfigurationsService } from '@icc/configurator/shared';
import {
    TranslateService,
    StateService,
} from '@icc/shared';
import { ConfiguratorTranslateService, MyMissingTranslationHandler } from './translate.service';
import { IccTranslateStore } from './translate-store';
import { CommonModule } from '@angular/common';
import { TranslateHttpLoader } from './translate-http-loader';
import { CurrentConfiguratorService, DrawService } from '@icc/configurator/shared';
import { ConfiguratorCurrentConfiguratorService } from './current-configurator.service';
import { ConfiguratorStepsService } from './step/steps.service';
import { ProfilesPriceService, ValidationService } from '@icc/construction-elements/configurator';
import { PriceModule, DiscountsAndMultipliersService } from '@icc/price/b2b';
import { WarrantyService } from '@icc/window-door/configurator';
import { ConfiguratorUserService } from './user.service';
import { UserService } from '@icc/shared/user.service';
import { CustomPricesService } from '@icc/shared/custom-price/custom-prices.service';
import { ConfiguratorCustomPricesService } from './custom-prices.service';
import { BrowserDiscountsAndMultipliersService } from '@icc/price/legacy';
import { ConfiguratorInfoService } from './info.service';
import { ShutterConfiguratorModule } from '@icc/shutter/configurator';
import { MatDialogModule } from '@angular/material/dialog';
import { MosquitoScreenConfiguratorModule } from '@icc/mosquito-screen/configurator';
import { PleatedBlindConfiguratorModule } from '@icc/pleated-blind/configurator';
import { ConfiguratorsAvailabilityService } from '@icc/configurator/shared';
import { HotkeyModule } from 'angular2-hotkeys';
import { AwningConfiguratorModule } from '@icc/awning/configurator';
import { SaveInformationComponent } from './save-information/save-information.component';
import { GarageDoorConfiguratorModule } from '@icc/garage-door/configurator';
import { TimeLimitModule } from '@icc/configurator/shared';
import { CoupledWindowConfiguratorModule } from '@icc/coupled-window/configurator';
import { ConstructionIssuesModalComponent } from './construction-issues-modal/construction-issues-modal.component';
import * as Sentry from "@sentry/angular";
import { AnomalyService } from './anomaly.service';
import { ClientInfoDialogComponent } from './client-info-dialog/client-info-dialog.component';
import { InfoService } from '@icc/shared/helpers';
import { CONFIGURATIONS_SERVICE } from '@icc/configurator/types';

export function HttpLoaderFactory(http: HttpClient, sharedFacade: SharedFacade, domSanitizer: DomSanitizer, config: AppConfigFactory) {
    return new TranslateHttpLoader(http, sharedFacade, domSanitizer, config);
}

export function loadOptionsFactory(initService: InitService) {
    return () => {
        return initService.loadOptionsFromServer();
    };
}

export function appConfigFactory(initService: InitService): () => Partial<AppConfig> {
    return () => {
        return initService.getAppConfig();
    };
}

const commonProviders: (Provider | EnvironmentProviders)[] = [
    InitService,
    ConfiguratorConfigurationsService,
    { provide: APP_CONFIG, useFactory: appConfigFactory, deps: [InitService] },
    { provide: TranslateStore, useClass: IccTranslateStore },
    EventBusService,
    ConfiguratorConfiguratorsDataService,
    ConfiguratorTranslateService,
    { provide: TranslateService, useExisting: ConfiguratorTranslateService },
    ConfiguratorCurrentConfiguratorService,
    { provide: CurrentConfiguratorService, useExisting: ConfiguratorCurrentConfiguratorService },
    ConfiguratorStepsService,
    { provide: StepsService, useExisting: ConfiguratorStepsService },
    ValidationService,
    ConfiguratorUserService,
    ProfilesPriceService,
    ConfiguratorCustomPricesService,
    { provide: CustomPricesService, useExisting: ConfiguratorCustomPricesService },
    WarrantyService,
    DrawService,
    ConfiguratorInfoService,
    { provide: InfoService, useExisting: ConfiguratorInfoService },
    ConfiguratorsAvailabilityService,
    AnomalyService,
];

const rootProviders: (Provider | EnvironmentProviders)[] = [
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(() => {
        const initializerFn = (loadOptionsFactory)(inject(InitService));
        return initializerFn();
      }),
    StateService,
    BrowserDiscountsAndMultipliersService,
    {
        provide: DiscountsAndMultipliersService,
        useExisting: BrowserDiscountsAndMultipliersService,
    },
    { provide: ConfigurationsService, useExisting: ConfiguratorConfigurationsService },
    { provide: CONFIGURATIONS_SERVICE, useExisting: ConfiguratorConfigurationsService },
    { provide: ConfiguratorsDataService, useExisting: ConfiguratorConfiguratorsDataService },
    { provide: LOCAL_DB_VERSION, useFactory: () => Number(localStorage.getItem(`localDbVersion`) || '0')},
    { provide: DatabaseManagerToken, useClass: ConfiguratorDatabaseManager },
    { provide: UserService, useExisting: ConfiguratorUserService },
    {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler(),
    },
];

@NgModule({
    declarations: [
        AppComponent,
        PreviewComponent,
        NotificationsPanelComponent,
        SaveInformationComponent,
        ConstructionIssuesModalComponent,
        ClientInfoDialogComponent,
    ],
    imports: [
        CommonModule,
        SharedModule,
        ConfiguratorUiModule,
        WindowDoorConfiguratorModule,
        ShutterConfiguratorModule,
        PleatedBlindConfiguratorModule,
        MosquitoScreenConfiguratorModule,
        CoupledWindowConfiguratorModule,
        IccDrawModule,
        HotkeyModule.forRoot(),
    ],
    exports: [
        AppComponent,
        PreviewComponent,
        NotificationsPanelComponent,
        SharedModule,
        WindowDoorConfiguratorModule,
        ConfiguratorUiModule,
        ShutterConfiguratorModule,
        PleatedBlindConfiguratorModule,
        MosquitoScreenConfiguratorModule,
        AwningConfiguratorModule,
        GarageDoorConfiguratorModule,
        CoupledWindowConfiguratorModule,
        IccDrawModule,
        SaveInformationComponent,
    ]
})
export class SharedAppModule {}

const imports: (any[] | Type<any> | ModuleWithProviders<{}>)[] = [
    SharedAppModule,
    ConfiguratorUiModule,
    AppRoutingModule,
    EffectsModule.forRoot([ConfiguratorEffects]),
    !environment.production ? StoreDevtoolsModule.instrument({connectInZone: true}) : [],
    StoreRouterConnectingModule.forRoot(),
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
    TranslateModule.forRoot({
        loader: {
            provide: TranslateLoader,
            useFactory: HttpLoaderFactory,
            deps: [HttpClient, SharedFacade, DomSanitizer, APP_CONFIG],
        },
        parser: {
            provide: TranslateParser,
            useClass: IccTranslateParser,
            deps: [APP_CONFIG],
        },
        missingTranslationHandler: {
            provide: MissingTranslationHandler,
            useClass: MyMissingTranslationHandler
        },
        compiler: {
            provide: TranslateCompiler,
            useClass: TranslateMessageFormatCompiler
        }
    }),
    PriceModule,
    TimeLimitModule,
];

@NgModule({
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        StoreModule.forRoot(
            {
                configurator: configuratorRootReducer,
            },
            {
                initialState: { configurator: { configurator: rootInitialState } },
                metaReducers: [],
                runtimeChecks: {
                    strictStateImmutability: false,
                    strictActionImmutability: false
                },
            }
        ),
        ...imports,
    ],
    providers: [
        ...commonProviders,
        ...rootProviders
    ],
    bootstrap: [AppComponent],
    exports: [],
})
export class AppModule {}

@NgModule({
    imports: [
        CommonModule,
        StoreModule.forFeature(
            'configurator',
            { configurator: configuratorReducer },
            {
                initialState: { configurator: rootInitialState },
                metaReducers: !environment.production ? [] : [],
            }
        ),
        ...imports,
    ],
    providers: commonProviders,
    bootstrap: [AppComponent],
    exports: [AppComponent],
})
export class NewConfiguratorModule {}

@NgModule({})
export class ConfiguratorLibModule {
    static forFeature(): ModuleWithProviders<NewConfiguratorModule> {
        return {
            ngModule: NewConfiguratorModule,
            providers: commonProviders,
        };
    }
}
